import type { IconName } from '@/components/ui/Icon';
import { type Permission, PERMISSIONS } from '@/features/auth/permissions';
import { ROUTES } from '@/routes/paths';

export interface NavItem {
  label: string;
  to: string;
  icon: IconName;
  /** Hidden unless the current user has this permission. */
  permission?: Permission;
}

export interface NavSection {
  id: string;
  title?: string;
  icon?: IconName;
  /** Top navbar: plain links, one dropdown under `title`, or only in the mobile menu. */
  display: 'links' | 'dropdown' | 'mobile-only';
  items: NavItem[];
}

/** Navbar navigation. Add an item here and a matching route in routes/router.tsx. */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'forms',
    title: 'Forms',
    display: 'links',
    items: [
      {
        label: 'Upload a form',
        to: ROUTES.documentUpload,
        icon: 'cloud-arrow-up',
        permission: PERMISSIONS.DOCUMENTS_UPLOAD,
      },
      {
        label: 'All forms',
        to: ROUTES.documents,
        icon: 'files',
        permission: PERMISSIONS.DOCUMENTS_READ,
      },
      { label: 'Dashboard', to: ROUTES.dashboard, icon: 'speedometer2' },
    ],
  },
  {
    id: 'admin',
    title: 'Administration',
    icon: 'gear',
    display: 'dropdown',
    items: [
      { label: 'Users', to: ROUTES.users, icon: 'people', permission: PERMISSIONS.USERS_READ },
      { label: 'Roles', to: ROUTES.roles, icon: 'shield-lock', permission: PERMISSIONS.ROLES_READ },
      {
        label: 'API keys',
        to: ROUTES.apiClients,
        icon: 'shield-check',
        permission: PERMISSIONS.API_CLIENTS_MANAGE,
      },
    ],
  },
  {
    // On desktop, Profile is in the user menu.
    id: 'account',
    title: 'Account',
    display: 'mobile-only',
    items: [{ label: 'Profile', to: ROUTES.account, icon: 'person-circle' }],
  },
];

/** Sections filtered by permission; empty sections are dropped. */
export function getVisibleNavSections(
  hasPermission: (permission?: Permission) => boolean,
): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => hasPermission(item.permission)),
  })).filter((section) => section.items.length > 0);
}

/**
 * The single nav target that matches `pathname` best: an exact match, or the longest `to` that
 * is a parent path (so /documents/42 highlights "All forms" but /documents/upload only "Upload").
 */
export function findActiveNavTarget(sections: NavSection[], pathname: string): string | null {
  let best: string | null = null;
  for (const item of sections.flatMap((section) => section.items)) {
    const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
    if (matches && (best === null || item.to.length > best.length)) best = item.to;
  }
  return best;
}
